<template>
    <div class="cr:mb-2 cr:font-medium cr:relative" v-click-outside="() => dropdownOpen = false">
        <div class="cr:relative cr:group">
            <div @click="toggleDropdown"
                 :class="{'cr:bg-slate-50': dropdownOpen}"
                 class="cr:cursor-pointer cr:w-full cr:rounded-lg cr:block cr:font-normal cr:border cr:border-slate-300 cr:py-2 cr:px-3 cr:h-11">
                {{ selectedLabel }}
            </div>
            <ul v-if="dropdownOpen"
                class="cr:absolute cr:z-10 cr:mt-1 cr:w-full cr:bg-white cr:border cr:border-slate-300 cr:rounded-lg cr:shadow-md cr:max-h-96 cr:overflow-y-auto">
                <li v-if="showDefaultOption" @click="selectOption(defaultOption.value)"
                    class="cr:cursor-pointer cr:px-3 cr:py-2 cr:hover:text-white cr:hover:font-semibold cr:text-gray-400"
                    :class="[hoverClasses]"
                >
                    {{ defaultOption.label }}
                </li>
                <li v-for="(option, index) in props.options"
                    :key="index"
                    @click="selectOption(option.value)"
                    class="cr:cursor-pointer cr:px-3 cr:py-2 cr:hover:text-white cr:hover:font-semibold"
                    :class="[hoverClasses, {'cr:font-bold cr:pointer-events-none': option.header, 'cr:ml-3': groupedView && !option.header}]"
                >
                    {{ option.label }}
                </li>
            </ul>
            <div class="cr:absolute cr:right-1 cr:top-[50%] cr:translate-y-[-50%] cr:pointer-events-none cr:bg-white cr:p-3 cr:group-hover:bg-slate-50">
                <svg class="cr:w-3" viewBox="0 0 16 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd"
                          d="M0.467894 0.406234C0.767576 0.156774 1.17398 0.0166356 1.59773 0.0166356C2.02148 0.0166356 2.42788 0.156774 2.72756 0.406234L7.99001 4.78808L13.2525 0.406234C13.3999 0.279142 13.5762 0.17777 13.7712 0.108032C13.9662 0.0382934 14.1759 0.00158557 14.388 5.02416e-05C14.6002 -0.00148509 14.8107 0.0321825 15.0071 0.099089C15.2035 0.165996 15.3819 0.264801 15.5319 0.38974C15.682 0.514679 15.8006 0.663249 15.881 0.826782C15.9613 0.990314 16.0018 1.16553 15.9999 1.34222C15.9981 1.5189 15.954 1.69351 15.8703 1.85586C15.7865 2.0182 15.6648 2.16503 15.5121 2.28778L9.11985 7.6104C8.82016 7.85986 8.41376 8 7.99001 8C7.56626 8 7.15986 7.85986 6.86017 7.6104L0.467894 2.28778C0.168302 2.03825 0 1.69985 0 1.34701C0 0.994165 0.168302 0.655768 0.467894 0.406234Z"
                          :fill="fillColor"/>
                </svg>

            </div>
        </div>
    </div>
</template>

<script setup lang="ts">

import type DropdownOption from "@/interfaces/DropdownOption.ts";
import {computed, ref} from "vue";
import {useRegistrationStore} from "@/stores/registration.ts";
import {ORIGINS} from "@/services/ConstantSerice.ts";

const registrationStore = useRegistrationStore();
const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: '',
    },
    options: {
        type: Array as () => DropdownOption[],
        default: [{label: '', value: ''}],
    },
    dropdownDisabled: {
        type: Boolean,
        default: false,
    },
    defaultOption: {
        type: Object as () => DropdownOption,
        default: {label: '-', value: ''}
    },
    selectedOption: {
        type: [String, Number],
        default: '',
    },
    showDefaultOption: {
        type: Boolean,
        default: true,
    },
});

const emit = defineEmits([
    'update:modelValue'
]);

const dropdownOpen = ref(false);

const toggleDropdown = () => {
    dropdownOpen.value = !dropdownOpen.value;
};

const selectOption = (value: string | number) => {
    emit("update:modelValue", value);
    dropdownOpen.value = false;
};

const selectedLabel = computed(() => {
    const selected = props.options.find(option => option.value === props.modelValue);
    return selected ? selected.label : props.defaultOption.label;
});

const groupedView = computed(() => props.options.some(option => !!option.header));

const hoverClasses = computed(() => {
    switch (registrationStore.registrationOrigin?.toLocaleLowerCase()) {
        case ORIGINS.BE:
            return 'cr:hover:bg-[var(--be-hover)]';
        case ORIGINS.KE:
            return 'cr:hover:bg-[var(--ke-hover)]';
        case ORIGINS.FIXR:
            return 'cr:hover:bg-[var(--fixr-hover)]';
        case ORIGINS.SR:
            return 'cr:hover:bg-[var(--sr-hover)]';
        case ORIGINS.RC:
            return 'cr:hover:bg-[var(--rc-hover)]';
        case ORIGINS.HM:
            return 'cr:hover:bg-[var(--hm-hover)]';    
        default:
            return 'cr:hover:bg-[var(--default-hover)]';
    }
});

const fillColor = computed(() => {
    switch (registrationStore.registrationOrigin?.toLocaleLowerCase()) {
        case ORIGINS.BE:
            return  'var(--be-primary)';
        case ORIGINS.KE:
            return  'var(--ke-primary)';
        case ORIGINS.FIXR:
            return  'var(--fixr-primary)';
        case ORIGINS.SR:
            return  'var(--sr-primary)';
        case ORIGINS.RC:
            return  'var(--rc-primary)'
        case ORIGINS.HM:
            return 'var(--hm-primary)';
        default:
            return 'var(--default-primary)';
    }
});

</script>
